import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import CampOpsNav from "@/components/CampOpsNav";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { QrCode, FileArchive, IdCard, Download, FileSpreadsheet } from "lucide-react";

export default function CampCredentials() {
  const { campId } = useParams();
  const [camp, setCamp] = useState(null);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get(`/admin/camps/${campId}/roster`).then(({ data }) => {
      setCamp(data.camp);
      setCount((data.players || []).length);
    }).catch(() => {});
  }, [campId]);

  const download = async (path, params, filename, key, openInline = false) => {
    setBusy(key);
    try {
      const res = await api.get(path, { params: { ...params, base_url: window.location.origin }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (openInline) {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Export ready");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Export failed");
    } finally { setBusy(""); }
  };

  const slug = camp?.slug || campId;

  return (
    <AdminLayout>
      <div className="p-8 max-w-5xl">
        <Link to="/admin/camps" className="label-overline text-slate-500 hover:text-[#E63946]">← All Camps</Link>
        <div className="label-overline text-[#E63946] mt-3">Credentials & QR Export</div>
        <h1 data-testid="credentials-title" className="font-display text-4xl uppercase tracking-tighter mt-2">{camp?.title || "Camp"}</h1>
        <p className="text-xs text-slate-500 mt-1">{count} registered player(s). Export QR assets and printable credential cards for Ottawa & Toronto ID Camp operations.</p>

        <CampOpsNav campId={campId} />

        <div className="grid md:grid-cols-3 gap-5">
          <Card icon={FileSpreadsheet} title="Camp Results — CSV" desc="Spreadsheet of every player's best verified metric per station (40-yd, exit velo, etc.) plus identity — for reporting, recruiting lists and post-camp analysis.">
            <ActionBtn testid="export-results-csv" busy={busy === "results"} onClick={() => download(`/admin/camps/${campId}/results.csv`, {}, `cbr-results-${slug}.csv`, "results")}>
              Download Results CSV
            </ActionBtn>
          </Card>

          <Card icon={QrCode} title="QR Codes — PNG" desc="ZIP of all player QR codes as high-res PNGs. Named PlayerNumber_Name_GradYear.png — drop into custom player cards, lanyards, badges or scorecards.">
            <ActionBtn testid="export-qr-png" busy={busy === "png"} onClick={() => download(`/admin/camps/${campId}/qr-codes.zip`, { format: "png" }, `cbr-qr-codes-${slug}-png.zip`, "png")}>
              Download PNG ZIP
            </ActionBtn>
          </Card>

          <Card icon={FileArchive} title="QR Codes — SVG" desc="ZIP of all player QR codes as scalable SVG vectors. Ideal for large-format printing and design tools (Illustrator, Canva, InDesign).">
            <ActionBtn testid="export-qr-svg" busy={busy === "svg"} onClick={() => download(`/admin/camps/${campId}/qr-codes.zip`, { format: "svg" }, `cbr-qr-codes-${slug}-svg.zip`, "svg")}>
              Download SVG ZIP
            </ActionBtn>
          </Card>

          <Card icon={IdCard} title="Credential Cards — PDF" desc="Printable PDF of every player's credential card with embedded QR. 8 badge-sized cards per page — optimized for lanyards, badge holders and name tags.">
            <ActionBtn testid="export-credentials-pdf" busy={busy === "pdf"} onClick={() => download(`/admin/camps/${campId}/credentials.pdf`, {}, `cbr-credentials-${slug}.pdf`, "pdf", true)}>
              Generate PDF
            </ActionBtn>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}

function Card({ icon: Icon, title, desc, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6 flex flex-col">
      <Icon className="w-8 h-8 text-[#E63946]" />
      <h3 className="font-display text-lg uppercase tracking-tight mt-4">{title}</h3>
      <p className="text-xs text-slate-500 mt-2 flex-1">{desc}</p>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function ActionBtn({ children, onClick, busy, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={busy}
      className="w-full inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">
      <Download className="w-4 h-4" /> {busy ? "Preparing…" : children}
    </button>
  );
}
